/**
 * Target-selection helpers operating directly on WireSimState (#1114).
 *
 * Pure functions the client uses to build ordinary `setTarget` inputs.
 * Directly operates on `WireSimState` and `WireEntity` from `@pit/protocol`
 * without constructing a synthetic SimState or calling `@pit/sim`.
 *
 * Rules:
 *   • `snapshot.entities[seat]` / seat lookup
 *   • entity `.team`, `.entityId`, `.hp` (liveness / friend-vs-foe)
 *   • entity `.targets.{hostile,friendly}` (cycle anchors)
 *   • entity `.auras` (stealth gate — stealth aura excludes from enemy targetability)
 *   • nearest enemy uses integer `distSq` with lowest-seat tie break
 */

import { distSq, imod } from "@pit/math";
import type { EntityId, WireAura, WireSimState } from "@pit/protocol";
import { STEALTH_AURA_DEF_ID } from "@pit/protocol";

type WireEntity = WireSimState["entities"][number];

/**
 * Is `auras` currently stealthed at `tick`? True iff it holds an unexpired
 * aura instance with `STEALTH_AURA_DEF_ID`.
 */
function isStealthedWire(auras: readonly WireAura[], tick: number): boolean {
  for (const a of auras) {
    if (a.def !== STEALTH_AURA_DEF_ID) continue;
    if (a.expiresAtTick === null || tick < a.expiresAtTick) return true;
  }
  return false;
}

/**
 * Can `viewer` target `target` at `tick`?
 * FALSE only for an ENEMY viewer (different team) of a STEALTHED target.
 * Self + allies (same team) can always target.
 */
function isTargetableByWire(viewer: WireEntity, target: WireEntity, tick: number): boolean {
  if (viewer.team === target.team) return true;
  return !isStealthedWire(target.auras, tick);
}

/**
 * Is the wire entity alive (hp > 0)?
 */
function isAliveWire(entity: WireEntity): boolean {
  return entity.hp > 0;
}

/**
 * Next enemy target for `seat` (Tab cycle → hostile slot), or `null` if none.
 *
 * Deterministic: purely a function of `(snapshot, seat)`.
 *   - Enemies are entities on a different team from `seat`.
 *   - Living enemies have `hp > 0`.
 *   - Stealthed enemies are untargetable by enemy viewers (#91 / #422).
 *   - If `targets.hostile` is currently set to a living, targetable enemy,
 *     the next cycle picks the NEXT living enemy after it (seat order, wrapping).
 *   - If `targets.hostile` is null or dead/untargetable, picks the FIRST living enemy in seat order.
 *   - If the current target is the only living enemy, returns the same id.
 */
export function nextEnemyTargetWire(
  snapshot: WireSimState,
  seat: number,
): EntityId | null {
  const actor = snapshot.entities[seat];
  if (actor === undefined) return null;

  const enemies: WireEntity[] = [];
  for (const e of snapshot.entities) {
    if (e.team === actor.team) continue;
    if (!isAliveWire(e)) continue;
    if (!isTargetableByWire(actor, e, snapshot.tick)) continue;
    enemies.push(e);
  }
  if (enemies.length === 0) return null;

  const current = actor.targets.hostile;
  if (current === null) {
    const first = enemies[0];
    if (first === undefined) return null;
    return first.entityId;
  }

  let idx = -1;
  for (let i = 0; i < enemies.length; i++) {
    const e = enemies[i];
    if (e === undefined) continue;
    if (e.entityId === current) {
      idx = i;
      break;
    }
  }
  if (idx < 0) {
    const first = enemies[0];
    if (first === undefined) return null;
    return first.entityId;
  }

  const nextIdx = imod(idx + 1, enemies.length);
  const next = enemies[nextIdx];
  if (next === undefined) return null;
  return next.entityId;
}

/**
 * #815: NEAREST living, targetable enemy for `seat` (Tab + auto-acquire →
 * hostile slot), or `null` if none exists.
 *
 * Determinism:
 *   - Integer-only `distSq` (@pit/math)
 *   - Iterates `snapshot.entities` in seat order
 *   - Strictly-less-than (`<`) keeps lowest-seat tie break
 *   - Skips same team, corpses, and stealthed enemies
 */
export function nearestEnemyTargetWire(
  snapshot: WireSimState,
  seat: number,
): EntityId | null {
  const actor = snapshot.entities[seat];
  if (actor === undefined) return null;

  let best: WireEntity | null = null;
  let bestDistSq = 0;
  for (const e of snapshot.entities) {
    if (e.team === actor.team) continue;
    if (!isAliveWire(e)) continue;
    if (!isTargetableByWire(actor, e, snapshot.tick)) continue;
    const d2 = distSq(actor.x, actor.y, e.x, e.y);
    if (best === null || d2 < bestDistSq) {
      best = e;
      bestDistSq = d2;
    }
  }
  return best === null ? null : best.entityId;
}

/**
 * #815: does `seat`'s hostile slot still hold a living, targetable enemy?
 * True iff `targets.hostile` names an entity that (a) exists, (b) is on a different
 * team, (c) is alive, and (d) is targetable by the actor (#91 / #422 stealth).
 */
export function hasLiveEnemyTargetWire(snapshot: WireSimState, seat: number): boolean {
  const actor = snapshot.entities[seat];
  if (actor === undefined) return false;
  const current = actor.targets.hostile;
  if (current === null) return false;
  for (const e of snapshot.entities) {
    if (e.entityId !== current) continue;
    if (e.team === actor.team) return false;
    if (!isAliveWire(e)) return false;
    return isTargetableByWire(actor, e, snapshot.tick);
  }
  return false;
}

/** Allies of `actor` (same team), self FIRST then ascending seat, dead included. */
function allyRosterWire(snapshot: WireSimState, actor: WireEntity): WireEntity[] {
  const others: WireEntity[] = [];
  for (const e of snapshot.entities) {
    if (e.team !== actor.team) continue;
    if (e.seat === actor.seat) continue;
    others.push(e);
  }
  return [actor, ...others];
}

/** Enemies of `actor` (different team), ascending seat order, dead included. */
function enemyRosterWire(snapshot: WireSimState, actor: WireEntity): WireEntity[] {
  const roster: WireEntity[] = [];
  for (const e of snapshot.entities) {
    if (e.team === actor.team) continue;
    roster.push(e);
  }
  return roster;
}

/**
 * Next ally target for `seat` (friendly cycle → friendly slot), or `null` if none.
 * Cycles in ally-roster order (self first, then other same-team allies in seat order).
 */
export function nextAllyTargetWire(
  snapshot: WireSimState,
  seat: number,
): EntityId | null {
  const actor = snapshot.entities[seat];
  if (actor === undefined) return null;

  const allies: WireEntity[] = [];
  for (const e of allyRosterWire(snapshot, actor)) {
    if (!isAliveWire(e)) continue;
    allies.push(e);
  }
  if (allies.length === 0) return null;

  const current = actor.targets.friendly;
  if (current === null) {
    const first = allies[0];
    if (first === undefined) return null;
    return first.entityId;
  }

  let idx = -1;
  for (let i = 0; i < allies.length; i++) {
    const e = allies[i];
    if (e === undefined) continue;
    if (e.entityId === current) {
      idx = i;
      break;
    }
  }
  if (idx < 0) {
    const first = allies[0];
    if (first === undefined) return null;
    return first.entityId;
  }

  const nextIdx = imod(idx + 1, allies.length);
  const next = allies[nextIdx];
  if (next === undefined) return null;
  return next.entityId;
}

/**
 * The `index`-th enemy for `seat` (arena direct-select → hostile slot), or
 * `null` when the roster slot is empty, holds a corpse, or is stealthed.
 */
export function enemyByIndexWire(
  snapshot: WireSimState,
  seat: number,
  index: number,
): EntityId | null {
  const actor = snapshot.entities[seat];
  if (actor === undefined) return null;
  const roster = enemyRosterWire(snapshot, actor);
  const target = roster[index];
  if (target === undefined) return null;
  if (!isAliveWire(target)) return null;
  if (!isTargetableByWire(actor, target, snapshot.tick)) return null;
  return target.entityId;
}

/**
 * The `index`-th ally for `seat` (party direct-select → friendly slot; index 0
 * = self), or `null` when the roster slot is empty or holds a corpse.
 */
export function allyByIndexWire(
  snapshot: WireSimState,
  seat: number,
  index: number,
): EntityId | null {
  const actor = snapshot.entities[seat];
  if (actor === undefined) return null;
  const roster = allyRosterWire(snapshot, actor);
  const target = roster[index];
  if (target === undefined) return null;
  if (!isAliveWire(target)) return null;
  return target.entityId;
}

/**
 * The direct-select INDEX at which `entityId` is the `seat`'s arena (enemy)
 * hotkey target, or `null` when `entityId` is not a live-targetable enemy of
 * `seat`. The inverse of `enemyByIndexWire`.
 */
export function enemyTargetIndexWire(
  snapshot: WireSimState,
  seat: number,
  entityId: EntityId,
): number | null {
  const actor = snapshot.entities[seat];
  if (actor === undefined) return null;
  const roster = enemyRosterWire(snapshot, actor);
  for (let i = 0; i < roster.length; i++) {
    const target = roster[i];
    if (target === undefined) continue;
    if (target.entityId !== entityId) continue;
    if (!isAliveWire(target)) return null;
    if (!isTargetableByWire(actor, target, snapshot.tick)) return null;
    return i;
  }
  return null;
}

/**
 * The direct-select INDEX at which `entityId` is the `seat`'s party (ally)
 * hotkey target (index 0 = self), or `null` when `entityId` is not a
 * live-targetable ally of `seat`. The ally mirror of `enemyTargetIndexWire`,
 * inverting `allyByIndexWire`.
 */
export function allyTargetIndexWire(
  snapshot: WireSimState,
  seat: number,
  entityId: EntityId,
): number | null {
  const actor = snapshot.entities[seat];
  if (actor === undefined) return null;
  const roster = allyRosterWire(snapshot, actor);
  for (let i = 0; i < roster.length; i++) {
    const target = roster[i];
    if (target === undefined) continue;
    if (target.entityId !== entityId) continue;
    if (!isAliveWire(target)) return null;
    return i;
  }
  return null;
}
